<template>
    <div class="ds-date-input">
        <div
            class="ds-date-input-trigger"
            :class="{ 'ds-date-input-trigger-open': isOpen }"
            @click="toggle">
            {{ displayValue }}
        </div>

        <div v-if="isOpen" class="ds-date-input-backdrop" @click="close" />

        <div v-if="isOpen" class="ds-calendar-popover" :class="{ 'ds-calendar-popover-wide': hasPresets }">
            <AppCalendar
                :model-value="calendarValue"
                :mode="mode"
                :precision="precision"
                :disabled-dates="disabledDates"
                :min-date="minDate"
                :initial-date="initialDate"
                :presets="hasPresets"
                @update:model-value="onSelect"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Dayjs } from 'dayjs'
import AppCalendar from './AppCalendar.vue'

type Mode = 'single' | 'range' | 'multi'
type Precision = 'day' | 'month'

const props = withDefaults(defineProps<{
    modelValue?: Dayjs | [Dayjs, Dayjs] | Dayjs[] | null
    mode?: Mode
    precision?: Precision
    displayFormat?: string
    disabledDates?: Dayjs[]
    minDate?: Dayjs
    placeholder?: string
    // RD-160: месяц, открываемый в календаре (по умолчанию — текущий).
    initialDate?: Dayjs
    // RD-160: быстрый выбор периода, только mode='range'.
    presets?: boolean
}>(), {
    mode: 'single',
    precision: 'day',
    placeholder: 'Выберите дату',
})

const emit = defineEmits<{
    'update:modelValue': [value: Dayjs | [Dayjs, Dayjs] | Dayjs[]]
}>()

const isOpen = ref(false)
const hasPresets = computed(() => props.presets && props.mode === 'range')
// Период: незавершённый выбор (только начало) живёт здесь и наружу не уходит;
// закрытие без второго клика оставляет прежнее значение (RD-160).
const rangeDraft = ref<[Dayjs, Dayjs] | null>(null)
const calendarValue = computed(() => (props.mode === 'range' && rangeDraft.value) || props.modelValue)

// «06.10 – 17.10.2026»; год у начала — только если годы разные.
function formatRange(start: Dayjs, end: Dayjs | null) {
    if (!end) return `${start.format('DD.MM.YYYY')} – …`
    const startFormat = start.isSame(end, 'year') ? 'DD.MM' : 'DD.MM.YYYY'
    return `${start.format(startFormat)} – ${end.format('DD.MM.YYYY')}`
}

const displayValue = computed(() => {
    if (!props.modelValue && !rangeDraft.value) return props.placeholder
    if (props.mode === 'single') {
        const value = props.modelValue as Dayjs
        if (props.displayFormat) return value.format(props.displayFormat)
        return props.precision === 'month' ? value.format('MMMM') : value.format('D.M.YYYY')
    }
    if (props.mode === 'range') {
        const [start, end] = (rangeDraft.value ?? props.modelValue) as [Dayjs, Dayjs | null]
        if (!start) return props.placeholder
        return formatRange(start, end)
    }
    if (props.mode === 'multi') {
        const dates = props.modelValue as Dayjs[]
        if (!dates.length) return props.placeholder
        return dates.map(d => d.format('D.M')).join(', ')
    }
    return props.placeholder
})

function close() {
    isOpen.value = false
    rangeDraft.value = null
}

function toggle() {
    if (isOpen.value) close()
    else isOpen.value = true
}

function onSelect(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    if (props.mode === 'range') {
        // Второй клик или быстрый выбор применяет полный период и закрывает popup.
        const [, end] = value as [Dayjs, Dayjs]
        if (!end) {
            rangeDraft.value = value as [Dayjs, Dayjs]
            return
        }
        emit('update:modelValue', value)
        close()
        return
    }
    emit('update:modelValue', value)
    // single — закрываем сразу; multi — не закрываем, пользователь сам закрывает
    if (props.mode === 'single') close()
}
</script>
